/* @jsxImportSource @emotion/react */

import React, { useMemo, useState } from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'motion/react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, useIntl } from 'react-intl'

import { ImgSanity } from '@slite/components'

import { WebsiteCard } from '../../components/Card/Card'
import { FilterBar, FilterPill } from '../../components/FilterBar'
import { Section } from '../../components/Section'
import { SlitePage } from '../../components/SlitePage'
import { WebsiteText } from '../../components/WebsiteText'
import { useRoutes } from '../../routes'
import type { BlogPost } from './+data'

const getBlogCategories = (intl: IntlShape) => [
  {
    title: intl.formatMessage({
      id: 'blog.category.all',
      defaultMessage: 'All',
    }),
    value: 'all',
    slug: 'all',
  },
  {
    title: intl.formatMessage({
      id: 'blog.category.enterpriseSearch',
      defaultMessage: 'Enterprise Search',
    }),
    value: 'enterpriseSearch',
    slug: 'enterprise-search',
  },
  {
    title: intl.formatMessage({
      id: 'blog.category.collectiveWisdom',
      defaultMessage: 'Collective Wisdom',
    }),
    value: 'collectiveWisdom',
    slug: 'collective-wisdom',
  },
  {
    title: intl.formatMessage({
      id: 'blog.category.workingTogether',
      defaultMessage: 'Working Together',
    }),
    value: 'workingTogether',
    slug: 'working-together',
  },
  {
    title: intl.formatMessage({
      id: 'blog.category.insideSlite',
      defaultMessage: 'Inside Slite',
    }),
    value: 'insideSlite',
    slug: 'inside-slite',
  },
]

export const BlogIndexLayout = ({
  blogPosts,
  selectedCategory,
}: {
  blogPosts: BlogPost[]
  selectedCategory: string
}) => {
  const intl = useIntl()
  const routes = useRoutes()
  const title = intl.formatMessage({
    id: 'blog.index.title',
    defaultMessage: 'The Slite Blog',
  })

  const description = intl.formatMessage({
    id: 'blog.index.description',
    defaultMessage:
      'We write about collaboration wisdom, working as a team and the future of knowledge management!',
  })

  const categories = getBlogCategories(intl)

  const [searchQuery, setSearchQuery] = useState('')

  const filteredPosts = useMemo(() => {
    if (!searchQuery.trim()) return blogPosts
    const query = searchQuery.toLowerCase()
    return blogPosts.filter((post) => post.title.toLowerCase().includes(query))
  }, [blogPosts, searchQuery])

  return (
    <SlitePage
      canonicalUrl={routes.blog.index()}
      background="moon-dust"
      title={title}
      description={description}
      ogImage={
        'https://cdn.sanity.io/images/dnsgqii5/production/a2df3c37ffc5a2d9dd8383c981d3e4e2cc7c4bcf-2400x1254.jpg'
      }
    >
      <Section
        background="moon-dust"
        className="text-align-center"
        containerBottomPadding={0}
      >
        <div className="grid-12">
          <div className="grid-span-8-large grid-offset-2-large grid-span-12 flexbox flexbox-column flexbox-align-center">
            <ImgSanity
              src="https://cdn.sanity.io/images/dnsgqii5/production/3182c5176914b5ba043ffebc5edda9e20e7022e2-794x794.png"
              alt={intl.formatMessage({
                id: 'blog.index.headerImageAlt',
                defaultMessage: 'Blog illustration',
              })}
              height={120}
              className="margin-bottom-32"
              priority
            />
            <WebsiteText variant="display" className="margin-bottom-16">
              <FormattedMessage
                defaultMessage="Readings about the future of team knowledge."
                id="blog.index.header"
              />
            </WebsiteText>
          </div>
        </div>
      </Section>
      <Section background="moon-dust" containerTopPadding={40}>
        <FilterBar
          background="moon-dust"
          className="margin-bottom-32"
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          searchLabel={intl.formatMessage({
            id: 'blog.index.search.ariaLabel',
            defaultMessage: 'Search articles',
          })}
          searchPlaceholder={intl.formatMessage({
            id: 'blog.index.search.placeholder',
            defaultMessage: 'Search articles',
          })}
        >
          {categories.map((cat) => (
            <FilterPill
              key={cat.value}
              href={
                cat.value === 'all' ? '/blog' : `/blog-category/${cat.slug}`
              }
              isActive={selectedCategory === cat.value}
            >
              {cat.title}
            </FilterPill>
          ))}
        </FilterBar>
        <AnimatePresence>
          <motion.div
            key={`${selectedCategory}-${searchQuery}`}
            className="grid-12 grid-gap-32"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {filteredPosts.length === 0 ? (
              <div className="grid-span-12 text-align-center padding-vertical-48">
                <WebsiteText variant="body" className="shade-20">
                  <FormattedMessage
                    defaultMessage="No articles found matching your search."
                    id="blog.index.no-results"
                  />
                </WebsiteText>
              </div>
            ) : (
              filteredPosts.map((post, index) => (
                <WebsiteCard
                  key={post._id}
                  href={routes.blog.post({ slug: post.slug.current })}
                  hoverOutline={false}
                  className={classNames(
                    'padding-horizontal-16 padding-top-16 padding-bottom-20 border-small border-alpha-shade-40 border-shade-05-on-hover',
                    index < 2
                      ? 'grid-span-6 grid-span-12-medium grid-span-10-small grid-offset-1-small'
                      : 'grid-span-4 grid-span-6-medium grid-span-10-small grid-offset-1-small'
                  )}
                >
                  <div
                    className="radius-larger margin-bottom-16"
                    css={css`
                      aspect-ratio: 16 / 9;
                      overflow: hidden;
                    `}
                  >
                    <ImgSanity
                      src={
                        post.thumbnailImage?.url ??
                        post.openGraphImage?.url ??
                        ''
                      }
                      alt={post.title}
                    />
                  </div>
                  <h5 className="font-family-title font-size-4 font-weight-medium margin-bottom-4 padding-bottom-2 shade-05">
                    {post.title}
                  </h5>
                  <p className="font-size-7 shade-10">
                    <FormattedMessage
                      id="blog.index.author"
                      defaultMessage="By {authorName}"
                      values={{ authorName: post.author.name }}
                    />
                  </p>
                </WebsiteCard>
              ))
            )}
          </motion.div>
        </AnimatePresence>
      </Section>
    </SlitePage>
  )
}
